import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  useMediaQuery,
  useTheme,
  Toolbar,
  AppBar,
  Alert,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocalShipping,
  Build,
  LocalGasStation,
  Group,
  DirectionsBus,
  Timeline,
  Speed,
  LocationOn,
  Receipt,
  Assignment,
  AccountBalance,
  TaxiAlert,
  Assignment as DocumentIcon,
  Assessment,
  Business,
  TrendingUp,
  AccountBalanceWallet,
  Email,
  Phone,
  LocationCity,
  LinkedIn,
  Twitter,
  Facebook,
  Security,
  Support,
  LocalTaxi,
  ContentCopy,
  Check,
  LinkOutlined,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import OTPLogin from "./OTPLogin";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import SEO from "./SEO";
import { LogoCarousel } from "./LogoCarousel";
import WhiteLogo from "../assets/logos/gaadikharcha-logo-white.png";

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  backgroundColor: "#fff",
}));

const HeroIllustration = () => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "450px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {/* Background Grid Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px",
          opacity: 0.3,
        }}
      />

      {/* Central Dashboard Mockup */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 2,
        }}
      >
        {/* Main Dashboard Panel */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.95)",
            borderRadius: 3,
            p: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            minWidth: 300,
          }}
        >
          {/* Header with Fleet Control */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              justifyContent: "center",
            }}
          >
            <DirectionsBus sx={{ fontSize: 28, color: "#1a365d", mr: 1 }} />
            <Typography variant="h6" sx={{ color: "#1a365d", fontWeight: 700 }}>
              P & L Dashboard
            </Typography>
          </Box>

          {/* Stats Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#f0f9ff",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "#1a365d", fontWeight: 700 }}
              >
                ₹1,24,000
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Monthly Earnings
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: "center",
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#fef3c7",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "#1a365d", fontWeight: 700 }}
              >
                ₹89,500
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Total Expenses
              </Typography>
            </Box>
          </Box>

          {/* Vehicle Status */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#10b981",
                }}
              />
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                12 Active
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#f59e0b",
                }}
              />
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                3 Maintenance
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#ef4444",
                }}
              />
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                1 Alert
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", mb: 0.5, display: "block" }}
            >
              Profit Margin: 28%
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 4,
                bgcolor: "#e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "28%",
                  height: "100%",
                  bgcolor: "#10b981",
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Floating Financial Icons */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          right: "10%",
          background: "rgba(255,255,255,0.9)",
          borderRadius: 3,
          p: 2,
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
          animation: "float 3s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px)" },
            "50%": { transform: "translateY(-10px)" },
          },
        }}
      >
        <TrendingUp sx={{ fontSize: 24, color: "#10b981" }} />
        <Typography
          variant="caption"
          sx={{ color: "#1a365d", fontWeight: 600, ml: 0.5 }}
        >
          +18% Growth
        </Typography>
      </Box>

      {/* Vehicle Fleet Indicator */}
      <Box
        sx={{
          position: "absolute",
          bottom: "20%",
          left: "8%",
          background: "rgba(255,255,255,0.9)",
          borderRadius: 3,
          p: 2,
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
          animation: "float 3s ease-in-out infinite 1s",
        }}
      >
        <LocalShipping sx={{ fontSize: 24, color: "#60a5fa" }} />
        <Typography
          variant="caption"
          sx={{ color: "#1a365d", fontWeight: 600, ml: 0.5 }}
        >
          16 Vehicles
        </Typography>
      </Box>

      {/* Expense Tracking Indicator */}
      <Box
        sx={{
          position: "absolute",
          top: "25%",
          left: "15%",
          background: "rgba(255,255,255,0.9)",
          borderRadius: 3,
          p: 2,
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
          animation: "float 3s ease-in-out infinite 2s",
        }}
      >
        <Assessment sx={{ fontSize: 24, color: "#f59e0b" }} />
        <Typography
          variant="caption"
          sx={{ color: "#1a365d", fontWeight: 600, ml: 0.5 }}
        >
          Live Reports
        </Typography>
      </Box>

      {/* Control Indicator */}
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          right: "15%",
          background: "rgba(255,255,255,0.9)",
          borderRadius: 3,
          p: 2,
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
          animation: "float 3s ease-in-out infinite 0.5s",
        }}
      >
        <AccountBalanceWallet sx={{ fontSize: 24, color: "#8b5cf6" }} />
        <Typography
          variant="caption"
          sx={{ color: "#1a365d", fontWeight: 600, ml: 0.5 }}
        >
          Full Control
        </Typography>
      </Box>

      {/* Background Decorative Elements */}
      <Box
        sx={{
          position: "absolute",
          top: "5%",
          left: "5%",
          opacity: 0.1,
          transform: "rotate(-15deg)",
        }}
      >
        <Business sx={{ fontSize: 60, color: "#ffffff" }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: "5%",
          right: "5%",
          opacity: 0.1,
          transform: "rotate(15deg)",
        }}
      >
        <Timeline sx={{ fontSize: 80, color: "#ffffff" }} />
      </Box>
    </Box>
  );
};

const featureCategories = [
  {
    category: "Business Management",
    icon: <Business fontSize="small" color="primary" />,
    features: [
      {
        icon: <Receipt fontSize="medium" color="primary" />,
        title: "Billing & Sales Management",
        description: "Complete invoicing, billing, and sales tracking",
      },
      {
        icon: <DocumentIcon fontSize="medium" color="primary" />,
        title: "Documentation & Reminders",
        description: "Digital docs with automated renewal alerts",
      },
      {
        icon: <AccountBalance fontSize="medium" color="primary" />,
        title: "Loan & Finance Tracking",
        description: "Track vehicle loans, EMIs, and payments",
      },
    ],
  },
  {
    category: "Operations",
    icon: <Build fontSize="small" color="primary" />,
    features: [
      {
        icon: <Build fontSize="medium" color="primary" />,
        title: "Maintenance & Repair Logs",
        description: "Detailed service records and scheduling",
      },
      {
        icon: <TaxiAlert fontSize="medium" color="primary" />,
        title: "Trip & Vehicle Expense Tracking",
        description: "Real-time expense and fuel monitoring",
      },
    ],
  },
  {
    category: "Financial Reports",
    icon: <Assessment fontSize="small" color="primary" />,
    features: [
      {
        icon: <Assessment fontSize="medium" color="primary" />,
        title: "Tax-Ready Reports: GST & More",
        description: "Instant compliance documents",
      },
      {
        icon: <TrendingUp fontSize="medium" color="primary" />,
        title: "Profit & Loss Reporting",
        description: "Detailed P&L statements",
      },
      {
        icon: <AccountBalanceWallet fontSize="medium" color="primary" />,
        title: "Balance Sheet Dashboard",
        description: "Real-time financial position",
      },
      {
        icon: <Business fontSize="medium" color="primary" />,
        title: "Company-Level Insights",
        description: "Comprehensive expense analysis",
      },
    ],
  },
];

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyContactLink = async () => {
    try {
      const contactUrl = `${window.location.origin}${window.location.pathname}#contact-us`;
      await navigator.clipboard.writeText(contactUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy link: ", err);
    }
  };

  const renderLoginOrMessage = () => {
    return (
      <Paper
        elevation={8}
        sx={{
          mt: 3,
          p: 4,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color="#1a365d"
            gutterBottom
          >
            Get Started Today
          </Typography>
        </Box>
        <OTPLogin />
      </Paper>
    );
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "GaadiKharcha.com",
    applicationCategory: "BusinessApplication",
    description:
      "Track, Manage, and Grow Your Fleet Business. Gaadi Ki Kamaai Aur Kharcha - Ab Aapke Control Mein!",
    url: "https://gaadikharcha.com",
    author: {
      "@type": "Organization",
      name: "GaadiKharcha.com",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    operatingSystem: "Web Browser",
    softwareVersion: "1.0",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "100",
    },
    featureList: [
      "Billing & Sales Management",
      "Documentation & Reminders",
      "Loan & Finance Tracking",
      "Maintenance & Repair Logs",
      "Trip & Vehicle Expense Tracking",
      "Tax-Ready Reports - GST & More",
      "Company-Level Expense Insights",
      "Profit & Loss Reporting",
      "Balance Sheet Dashboard",
    ],
  };

  return (
    <>
      <SEO
        title="GaadiKharcha.com - Gaadi Ki Kamaai Aur Kharcha - Ab Aapke Control Mein!"
        description="Track, Manage, and Grow Your Fleet Business. Gaadi Ki Kamaai Aur Kharcha - Ab Aapke Control Mein!"
        keywords="vehicle fleet management, transport business software, booking tracking system, maintenance management, fuel monitoring, driver management, gaadi kharcha, vehicle expense tracking, fleet analytics, transport logistics, bus management system, truck fleet software"
        structuredData={structuredData}
      />
      <Box sx={{ minHeight: "100vh" }}>
        {/* Header */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: "#1a365d",
            borderBottom: "1px solid #2d4a69",
            borderRadius: 0,
            zIndex: 1200,
          }}
        >
          <Toolbar
            sx={{
              justifyContent: "space-between",
              minHeight: "72px",
            }}
          >
            {/* Left side - GaadiKharcha.com with menu icon */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={WhiteLogo}
                alt="GaadiKharcha"
                style={{ width: "120px", height: "auto" }}
              />
            </Box>
          </Toolbar>
        </AppBar>
        {/* Hero Section */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #1a365d 0%, #2c5282 50%, #3182ce 100%)",
            py: { xs: 8, md: 12 },
            display: "flex",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="%23ffffff" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>\')',
              opacity: 0.1,
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Grid container spacing={4} alignItems="flex-end">
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#fdc003",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      fontSize: "0.8rem",
                    }}
                  >
                    Track, Manage, and Grow Your Fleet Business
                  </Typography>
                </Box>
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  fontWeight={800}
                  gutterBottom
                  sx={{
                    color: "#ffffff",
                    lineHeight: 1.1,
                    mb: 2,
                  }}
                >
                  Enterprise Login
                </Typography>
                {renderLoginOrMessage()}
              </Grid>
              <Grid item xs={12} md={6}>
                <HeroIllustration />
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} sx={{ mt: 4 }}>
            </Grid>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            backgroundColor: "#0f172a",
            color: "#e2e8f0",
            pt: { xs: 6, md: 8 },
            pb: 3,
          }}
        >
          <Container maxWidth="lg">
            {/* Main Footer Content */}
            <Grid
              container
              spacing={4}
              justifyContent="space-between"
              alignItems="start"
              flexDirection={{ xs: "column", md: "row" }}
            >
              {/* Company Info */}
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={WhiteLogo}
                      alt="GaadiKharcha"
                      style={{
                        width: "150px",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#94a3b8",
                      lineHeight: 1.6,
                      mb: 3,
                    }}
                  >
                    Platform from Aequitous.
                  </Typography>

                  {/* Trust Badges */}
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Security sx={{ fontSize: 16, color: "#60a5fa" }} />
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        SSL Secured
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Contact Info */}
              <Grid
                item
                xs={12}
                md={4}
                id="contact-us"
                sx={{
                  scrollMarginTop: "80px", // Offset for any fixed headers
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#ffffff",
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    Contact Us
                  </Typography>
                  <Tooltip
                    title={
                      linkCopied ? "Link copied!" : "Copy link to this section"
                    }
                    placement="top"
                  >
                    <IconButton
                      onClick={handleCopyContactLink}
                      size="small"
                      sx={{
                        color: linkCopied ? "#10b981" : "#94a3b8",
                        "&:hover": {
                          color: "#60a5fa",
                          backgroundColor: "rgba(96, 165, 250, 0.1)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {linkCopied ? (
                        <Check sx={{ fontSize: 18 }} />
                      ) : (
                        <LinkOutlined sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Email sx={{ fontSize: 20, color: "#60a5fa" }} />
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      info@aequitous.com
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Phone sx={{ fontSize: 20, color: "#60a5fa" }} />
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      +91 22 4515 2489
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LocationCity sx={{ fontSize: 20, color: "#60a5fa" }} />
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      Mumbai, Maharashtra, India
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Bottom Footer */}
            <Box
              sx={{
                borderTop: "1px solid #334155",
                mt: 6,
                pt: 4,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                © {new Date().getFullYear()} Aequitous Pvt. Ltd. All rights
                reserved.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingPage;
